// Transport for the AI Control Center admin surface.
// Targets pos-agents-service (agenticpos product, us-west-1).
//
// This is the ONLY file in the AI Control Center that knows a hostname.
// detectStage/agentsUrl take optional arguments so they are testable without
// a DOM — the zero-argument form reads the ambient environment as before.

import { authedRequest, tryRefreshToken } from '../apiService';

export type Stage = 'prod' | 'staging' | 'dev';

// Super-admin UI is agenticpos-scoped in this view — future work could
// multiplex across products via a product selector in the URL.
export const AGENTICPOS_DOMAINS: Record<Stage, string> = {
  dev: 'dev.rubytill.com',
  staging: 'stage.rubytill.com',
  prod: 'rubytill.com',
};

const ambientViteStage = (): string => {
  try {
    return typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_STAGE ?? '' : '';
  } catch (_) {
    return '';
  }
};

const ambientHostname = (): string =>
  typeof window !== 'undefined' ? window.location.hostname : '';

export function detectStage(hostname?: string, viteStage?: string): Stage {
  const vs = viteStage ?? ambientViteStage();
  if (vs === 'prod' || vs === 'staging' || vs === 'dev') return vs;
  const host = hostname ?? ambientHostname();
  if (host.includes('dev.')) return 'dev';
  if (host.includes('staging.')) return 'staging';
  if (host === 'localhost' || host === '127.0.0.1') return 'dev';
  return 'prod';
}

export function agentsUrl(path: string, stage?: Stage): string {
  return `https://pos-agents.api.${AGENTICPOS_DOMAINS[stage ?? detectStage()]}${path}`;
}

async function rawFetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(agentsUrl(path), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${body}`.trim());
  }
  const data = await res.json();
  if (data && typeof data.body === 'string') {
    try { return JSON.parse(data.body) as T; } catch { return data as T; }
  }
  return data as T;
}

/**
 * GET through authedRequest so callers get auto token refresh + 401 retry.
 * An explicit authHeaderOverride is not the current Cognito JWT, so those
 * (legacy) callers keep the raw fetch path.
 */
export async function adminGet<T>(
  path: string,
  authHeaderOverride?: string,
  opts?: { skipSessionExpiredOn401?: boolean },
): Promise<T> {
  if (authHeaderOverride) return rawFetchJson<T>(path, { headers: { Authorization: authHeaderOverride } });
  return authedRequest<T>(agentsUrl(path), opts);
}

export async function adminSend<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
  authHeaderOverride?: string,
): Promise<T> {
  const init: RequestInit = {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  if (authHeaderOverride) {
    return rawFetchJson<T>(path, { ...init, headers: { Authorization: authHeaderOverride } });
  }
  return authedRequest<T>(agentsUrl(path), init);
}

/** Binary download (CSV/PDF export) with one refresh-and-retry on 401. */
export async function authedBlob(path: string, authHeaderOverride?: string): Promise<Blob> {
  const url = agentsUrl(path);
  const buildHeaders = (): Record<string, string> => {
    if (authHeaderOverride) return { Authorization: authHeaderOverride };
    const t = localStorage.getItem('jwtToken');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  let res = await fetch(url, { headers: buildHeaders() });
  if (res.status === 401 && !authHeaderOverride) {
    const refreshed = await tryRefreshToken();
    if (refreshed) res = await fetch(url, { headers: { Authorization: `Bearer ${refreshed}` } });
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`authedBlob: ${res.status} ${body}`.trim());
  }
  return res.blob();
}

export { rawFetchJson };
